/**
 * v132 — Range Log user preferences hook.
 *
 * Persists the user's display unit (in / cm) and capture mode (quick / advanced)
 * to localStorage. Sandbox-safe.
 */

import { useCallback, useEffect, useState } from 'react';
import type { CaptureMode, DisplayUnit } from './v3-schema';
import { V132_KEYS } from './v3-schema';

function safeGet(k: string): string | null {
  try { return typeof window === 'undefined' ? null : window.localStorage.getItem(k); }
  catch { return null; }
}
function safeSet(k: string, v: string): void {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(k, v); }
  catch { /* ignore */ }
}

export function useRangePrefs(): {
  unit: DisplayUnit;
  setUnit: (u: DisplayUnit) => void;
  captureMode: CaptureMode;
  setCaptureMode: (m: CaptureMode) => void;
} {
  const [unit, setUnitState] = useState<DisplayUnit>(() => {
    const v = safeGet(V132_KEYS.displayUnit);
    return v === 'cm' ? 'cm' : 'in';
  });
  const [captureMode, setCaptureModeState] = useState<CaptureMode>(() => {
    const v = safeGet(V132_KEYS.captureMode);
    return v === 'advanced' ? 'advanced' : 'quick';
  });

  useEffect(() => safeSet(V132_KEYS.displayUnit, unit), [unit]);
  useEffect(() => safeSet(V132_KEYS.captureMode, captureMode), [captureMode]);

  return {
    unit,
    setUnit: useCallback((u: DisplayUnit) => setUnitState(u), []),
    captureMode,
    setCaptureMode: useCallback((m: CaptureMode) => setCaptureModeState(m), []),
  };
}
